import {
    uncommitProductConfiguration,
    UncommitProductConfigurationApiV1Parameter,
} from "@husky-hrc/Framework/Services/ProductConfigServiceV1";
import mapApiResult from "@husky-hrc/Framework/Handlers/Data/ProductConfig/MapApiResult";
import { ProductConfigDto, ProductConfigModel, ResponseModel } from "@husky-hrc/Framework/Types/ApiModel";
import Constants from "@husky-hrc/Utilities/Constants";
import isApiError from "@insite/client-framework/Common/isApiError";
import throwErrorIfTesting from "@insite/client-framework/Common/ThrowErrorIfTesting";
import {
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    Handler,
    HasOnError,
    HasOnException,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";

type Parameter = UncommitProductConfigurationApiV1Parameter &
    HasOnSuccess<ProductConfigModel> &
    HasOnException<ResponseModel> &
    HasOnError;
type Props = {
    apiParameter: UncommitProductConfigurationApiV1Parameter;
    apiResult: ProductConfigDto;
    parsedResult: ProductConfigModel;
    exception?: boolean;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginUncommitProductConfig: HandlerType = props => {
    throwErrorIfTesting();

    props.dispatch({
        type: Constants.Actions.BeginUpdateProductConfig,
        id: props.parameter.productId,
    } as any);
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
    };
};

export const RequestDataFromApi: HandlerType = async props => {
    try {
        props.apiResult = await uncommitProductConfiguration(props.apiParameter);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            try {
                props.apiResult = JSON.parse(error.errorMessage);
                props.exception = true;
            } catch {
                props.parameter.onError?.();
                return false;
            }
        } else {
            props.parameter.onError?.();
            return false;
        }
    }
};

export const MapApiResultToModel: HandlerType = async props => {
    props.parsedResult = await executeAwaitableHandlerChain(mapApiResult, { productConfigDto: props.apiResult }, props);
};

export const DispatchCompleteUncommitProductConfig: HandlerType = props => {
    props.dispatch({
        type: Constants.Actions.CompleteUpdateProductConfig,
        model: props.parsedResult,
        id: props.parameter.productId,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.exception) {
        props.parameter.onSuccess?.(props.parsedResult);
    }
};

export const ExecuteOnExceptionCallback: HandlerType = props => {
    if (props.exception) {
        props.parameter.onException?.(props.parsedResult.response);
    }
};

export const chain = [
    DispatchBeginUncommitProductConfig,
    PopulateApiParameter,
    RequestDataFromApi,
    MapApiResultToModel,
    DispatchCompleteUncommitProductConfig,
    ExecuteOnSuccessCallback,
    ExecuteOnExceptionCallback,
];

const uncommitProductConfig = createHandlerChainRunner(chain, "UncommitProductConfig");
export default uncommitProductConfig;
